import { Invoice2, LineItem2 } from "src/docuclipper/DocuclipperTypes";

// Match list-recent-invoice-mismatches.ts: $0.02 absorbs 1¢ rounding on either side.
export const INVOICE_TOL = 0.02;

const toNumber = (v: any): number => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : 0;
};

export type InvoiceConsistency = {
  total: number;
  subTotal: number;
  tax: number;
  linesSum: number;
  numLines: number;
  // header: subTotal + tax == total
  headerDelta: number;
  headerApplicable: boolean;
  headerMismatch: boolean;
  // lines: Σ lines[].price == subTotal
  linesDelta: number;
  linesApplicable: boolean;
  linesMismatch: boolean;
  // overall
  isApplicable: boolean;
  isConsistent: boolean;
};

export function checkInvoiceConsistency(
  invoice: Pick<Invoice2, "total" | "subTotal" | "tax" | "lines"> | null | undefined
): InvoiceConsistency {
  const total = toNumber(invoice?.total);
  const subTotal = toNumber(invoice?.subTotal);
  const tax = toNumber(invoice?.tax);
  const lines = Array.isArray(invoice?.lines) ? invoice!.lines : [];
  const numLines = lines.length;
  const linesSum = lines.reduce(
    (acc: number, l: LineItem2) => acc + toNumber((l as any)?.price),
    0
  );

  const headerDelta = total - (subTotal + tax);
  const headerApplicable =
    (total !== 0 || linesSum !== 0) && (subTotal !== 0 || tax !== 0);
  const headerMismatch =
    headerApplicable && Math.abs(headerDelta) > INVOICE_TOL;

  const linesDelta = subTotal - linesSum;
  const linesApplicable = numLines > 0 && subTotal !== 0;
  const linesMismatch = linesApplicable && Math.abs(linesDelta) > INVOICE_TOL;

  const isApplicable = headerApplicable || linesApplicable;
  const isConsistent = isApplicable && !headerMismatch && !linesMismatch;

  return {
    total,
    subTotal,
    tax,
    linesSum,
    numLines,
    headerDelta,
    headerApplicable,
    headerMismatch,
    linesDelta,
    linesApplicable,
    linesMismatch,
    isApplicable,
    isConsistent,
  };
}

export function formatDelta(delta: number): string {
  const sign = delta >= 0 ? "+" : "−";
  return `${sign}${Math.abs(delta).toFixed(2)}`;
}
